import { api } from "@/lib/api";

// Campus Infrastructure API client.
// All calls scope by `campus_id`. Backend enforces tenant/institution isolation.
export const InfraAPI = {
  dashboard: (campusId) => api.get(`/infrastructure/dashboard/${campusId}`).then((r) => r.data),

  getProfile: (campusId) => api.get(`/infrastructure/profile/${campusId}`).then((r) => r.data),
  saveProfile: (campusId, patch) => api.put(`/infrastructure/profile/${campusId}`, patch).then((r) => r.data),

  // Buildings ↔ Wings ↔ Floors ↔ Rooms
  listBuildings: (campusId) => api.get(`/infrastructure/buildings`, { params: { campus_id: campusId } }).then((r) => r.data),
  createBuilding: (payload) => api.post(`/infrastructure/buildings`, payload).then((r) => r.data),
  updateBuilding: (id, patch) => api.patch(`/infrastructure/buildings/${id}`, patch).then((r) => r.data),
  deleteBuilding: (id) => api.delete(`/infrastructure/buildings/${id}`),

  listWings: (campusId, buildingId) =>
    api.get(`/infrastructure/wings`, { params: { campus_id: campusId, building_id: buildingId } }).then((r) => r.data),
  createWing: (payload) => api.post(`/infrastructure/wings`, payload).then((r) => r.data),
  updateWing: (id, patch) => api.patch(`/infrastructure/wings/${id}`, patch).then((r) => r.data),
  deleteWing: (id) => api.delete(`/infrastructure/wings/${id}`),

  listFloors: (campusId, buildingId) =>
    api.get(`/infrastructure/floors`, { params: { campus_id: campusId, building_id: buildingId } }).then((r) => r.data),
  createFloor: (payload) => api.post(`/infrastructure/floors`, payload).then((r) => r.data),
  updateFloor: (id, patch) => api.patch(`/infrastructure/floors/${id}`, patch).then((r) => r.data),
  deleteFloor: (id) => api.delete(`/infrastructure/floors/${id}`),

  listRooms: (campusId, params = {}) =>
    api.get(`/infrastructure/rooms`, { params: { campus_id: campusId, ...params } }).then((r) => r.data),
  createRoom: (payload) => api.post(`/infrastructure/rooms`, payload).then((r) => r.data),
  updateRoom: (id, patch) => api.patch(`/infrastructure/rooms/${id}`, patch).then((r) => r.data),
  deleteRoom: (id) => api.delete(`/infrastructure/rooms/${id}`),
  bulkGenerateRooms: (payload) => api.post(`/infrastructure/rooms/bulk-generate`, payload).then((r) => r.data),

  // Uploads
  upload: (file, campusId) => {
    const form = new FormData();
    form.append("file", file);
    return api
      .post(`/infrastructure/upload/${campusId}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },
  fileUrl: (fileId) => `${api.defaults.baseURL}/infrastructure/file/${fileId}`,

  // Reusable read APIs for downstream modules
  examReadyRooms: (campusId, minCapacity = 0) =>
    api.get(`/infrastructure/exam-ready-rooms/${campusId}`, { params: { min_capacity: minCapacity } }).then((r) => r.data),

  // Reports (Phase 4b)
  listReports: () => api.get(`/infrastructure/reports/kinds`).then((r) => r.data),
  reportUrl: (kind, campusId) => `${api.defaults.baseURL}/infrastructure/reports/${kind}/${campusId}.csv`,
  templateUrl: (kind) => `${api.defaults.baseURL}/infrastructure/import/template/${kind}.csv`,

  importRooms: (campusId, file) => {
    const form = new FormData();
    form.append("file", file);
    return api
      .post(`/infrastructure/import/rooms/${campusId}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },
};

export const ROOM_TYPES = [
  { value: "classroom", label: "Classroom" },
  { value: "lecture_hall", label: "Lecture Hall" },
  { value: "seminar_hall", label: "Seminar Hall" },
  { value: "physics_lab", label: "Physics Lab" },
  { value: "chemistry_lab", label: "Chemistry Lab" },
  { value: "biology_lab", label: "Biology Lab" },
  { value: "computer_lab", label: "Computer Lab" },
  { value: "language_lab", label: "Language Lab" },
  { value: "robotics_lab", label: "Robotics Lab" },
  { value: "ai_lab", label: "AI Lab" },
  { value: "library", label: "Library" },
  { value: "auditorium", label: "Auditorium" },
  { value: "conference_hall", label: "Conference Hall" },
  { value: "principal_office", label: "Principal Office" },
  { value: "dean_office", label: "Dean Office" },
  { value: "staff_room", label: "Staff Room" },
  { value: "medical_room", label: "Medical Room" },
  { value: "meeting_room", label: "Meeting Room" },
  { value: "hostel_room", label: "Hostel Room" },
  { value: "exam_hall", label: "Exam Hall" },
  { value: "store_room", label: "Store Room" },
  { value: "server_room", label: "Server Room" },
  { value: "utility_room", label: "Utility Room" },
  { value: "other", label: "Other" },
];

export const CARD_SPECS = [
  { key: "campus_profile", label: "Campus Profile", icon: "Landmark", color: "sky", summary: "General information about your campus, contact details and capacity." },
  { key: "buildings", label: "Buildings", icon: "Building2", color: "indigo", summary: "Complete hierarchy of Buildings → Wings → Floors → Rooms." },
  { key: "academic_facilities", label: "Academic Facilities", icon: "GraduationCap", color: "violet", summary: "Labs, libraries, seminar halls, research spaces and more." },
  { key: "sports_facilities", label: "Sports Facilities", icon: "Trophy", color: "amber", summary: "Fields, courts, pools, gymnasium and athletics." },
  { key: "student_facilities", label: "Student Facilities", icon: "Users", color: "emerald", summary: "Transport, hostel, medical room, cafeteria, parking and lounges." },
  { key: "smart_campus", label: "Smart Campus", icon: "Wifi", color: "cyan", summary: "WiFi, smart boards, RFID, biometric, IoT and apps." },
  { key: "safety_security", label: "Safety & Security", icon: "Shield", color: "rose", summary: "Fire safety, evacuation, accessibility and compliance." },
  { key: "utilities", label: "Utilities", icon: "Zap", color: "yellow", summary: "Electricity, water, internet, waste and sustainability." },
  { key: "assets", label: "Assets", icon: "Monitor", color: "slate", summary: "Track computers, projectors, furniture and other physical assets." },
  { key: "documents", label: "Documents & Compliance", icon: "FileText", color: "teal", summary: "Building plans, NOCs, insurance, audit reports and certificates." },
  { key: "maintenance", label: "Maintenance", icon: "Wrench", color: "orange", summary: "Tickets, vendors, schedules and inspection history." },
  { key: "reports", label: "Reports", icon: "BarChart3", color: "fuchsia", summary: "Infrastructure summary, occupancy, exam readiness and health." },
];
